import { useEffect, useRef, useState } from "react";
import { Button } from "@web/ui/Button";
import { Kbd } from "@web/ui/Kbd";

/** Presets are natural language; the server parses them in the owner's time zone. */
export const SNOOZE_PRESETS = [
  { key: "1", label: "Later today", until: "in 3 hours" },
  { key: "2", label: "Tomorrow morning", until: "tomorrow 9am" },
  { key: "3", label: "Monday morning", until: "monday 9am" },
] as const;

export function SnoozePicker({
  onPick,
  onClose,
  bindDigits = false,
  title = "Bring this back…",
}: {
  onPick: (until: string, label: string) => void;
  onClose: () => void;
  /** Handle 1–3 here (screens without card keys, like Tasks). The card binds them itself. */
  bindDigits?: boolean;
  title?: string;
}) {
  const [text, setText] = useState("");
  const firstRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    firstRef.current?.focus({ preventScroll: true });
    firstRef.current?.closest("[role=group]")?.scrollIntoView({ block: "nearest" });
  }, []);
  return (
    <div
      role="group"
      aria-label="Snooze until"
      className="flex flex-col gap-2 rounded-xl border border-line bg-bg px-4 py-3"
      onKeyDown={(e) => {
        if (!bindDigits || e.metaKey || e.ctrlKey || e.altKey) return;
        const tag = (e.target as HTMLElement).tagName;
        if (tag === "INPUT" || tag === "TEXTAREA") return;
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          onClose();
          return;
        }
        const preset = SNOOZE_PRESETS.find((p) => p.key === e.key);
        if (!preset) return;
        e.preventDefault();
        e.stopPropagation();
        onPick(preset.until, preset.label);
      }}
    >
      <p className="text-sm font-medium text-ink">{title}</p>
      <div className="flex flex-wrap gap-2">
        {SNOOZE_PRESETS.map((p, i) => (
          <Button
            key={p.key}
            ref={i === 0 ? firstRef : undefined}
            size="sm"
            data-snooze-preset={p.key}
            onClick={() => onPick(p.until, p.label)}
          >
            {p.label}
            <Kbd>{p.key}</Kbd>
          </Button>
        ))}
      </div>
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (text.trim()) onPick(text.trim(), text.trim());
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault();
              onClose();
            }
          }}
          placeholder="Or type a time, like “friday 2pm”"
          aria-label="Snooze until"
          className="h-8 min-w-0 flex-1 rounded-lg border border-line-strong bg-paper px-3 text-sm text-ink placeholder:text-faint"
        />
        <Button size="sm" type="submit" disabled={!text.trim()}>
          Snooze
        </Button>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </form>
    </div>
  );
}
